import { memo, useCallback, useEffect, useState } from 'react';
import { FormattedInlineText } from './formatted-inline-text';
import { Stamp, type StampProps } from './stamp';
import {
  DEFAULT_RECORD_CARD_CONTINUE_TEXT,
  formatRecordCardSerialNumber,
} from '../systems/record-card-content';
import { isMotionReduced } from '../systems/accessibility-settings';
import styles from './record-card-screen.module.css';

export interface RecordCardScreenProps {
  title: string;
  subtitle?: string;
  summary?: string;
  portraitSrc?: string;
  matricule?: string;
  serialSerie?: string;
  serialNumber?: string | null;
  dangerScore?: number;
  dangerMax?: number;
  dangerWord?: string;
  taken?: string[];
  saved?: string[];
  stamp?: Pick<StampProps, 'text' | 'sub'>;
  continueText?: string;
  nextHint?: string;
  background?: string;
  onContinue: () => void;
}

export const RecordCardScreen = memo(function RecordCardScreen({
  title,
  subtitle,
  summary,
  portraitSrc,
  matricule,
  serialSerie,
  serialNumber,
  dangerScore,
  dangerMax = 10,
  dangerWord,
  taken = [],
  saved = [],
  stamp,
  continueText = DEFAULT_RECORD_CARD_CONTINUE_TEXT,
  nextHint,
  background,
  onContinue,
}: RecordCardScreenProps) {
  const [instant, setInstant] = useState(isMotionReduced);
  const serialDisplay = serialNumber ?? null;
  const showDanger = dangerScore !== undefined && dangerScore !== null;
  const showBilan = taken.length > 0 || saved.length > 0;

  useEffect(() => {
    setInstant(isMotionReduced());
  }, [title, background, stamp?.text, stamp?.sub]);

  const handleOverlayClick = useCallback(() => {
    setInstant(true);
  }, []);

  const handleContinueClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();
      onContinue();
    },
    [onContinue],
  );

  const rootClass = [styles.overlay, instant ? styles.instant : styles.play].join(' ');

  return (
    <div
      className={rootClass}
      role="dialog"
      aria-modal="true"
      aria-labelledby="record-card-title"
      onClick={handleOverlayClick}
    >
      {background ? (
        <div
          className={styles.background}
          style={{ backgroundImage: `url(${background})` }}
          aria-hidden
        />
      ) : null}
      <div className={styles.veil} aria-hidden />

      <div className={`${styles.card} cn-zoom-ui`}>
        <span className={`${styles.corner} ${styles.cornerHg}`} aria-hidden />
        <span className={`${styles.corner} ${styles.cornerHd}`} aria-hidden />
        <span className={`${styles.corner} ${styles.cornerBg}`} aria-hidden />
        <span className={`${styles.corner} ${styles.cornerBd}`} aria-hidden />

        {(serialSerie || serialDisplay) && (
          <div className={styles.serie}>
            {serialSerie ? <span className={styles.serieName}>{serialSerie}</span> : <span />}
            {serialDisplay ? <span className={styles.serieNum}>{serialDisplay}</span> : null}
          </div>
        )}

        <div className={styles.top}>
          {portraitSrc ? (
            <div className={styles.portrait}>
              <img src={portraitSrc} alt="" />
              {matricule ? <span className={styles.matricule}>{matricule}</span> : null}
            </div>
          ) : null}
          <div className={styles.ident}>
            <h1 id="record-card-title" className={styles.name}>
              <FormattedInlineText text={title} />
            </h1>
            {subtitle ? (
              <p className={styles.dite}>
                <FormattedInlineText text={subtitle} />
              </p>
            ) : null}
            {summary ? (
              <p className={styles.fait}>
                <FormattedInlineText text={summary} />
              </p>
            ) : null}
            {showDanger ? (
              <div className={styles.danger}>
                <span className={styles.dangerScore}>{dangerScore}</span>
                <span className={styles.dangerSur}>/{dangerMax}</span>
                <span className={styles.dangerSide}>
                  <span className={styles.dangerLabel}>DANGEROSITÉ</span>
                  {dangerWord ? (
                    <span className={styles.dangerWord}>
                      « <FormattedInlineText text={dangerWord} /> »
                    </span>
                  ) : null}
                </span>
              </div>
            ) : null}
          </div>
        </div>

        {showBilan ? (
          <div className={styles.bilan}>
            <div className={styles.bilanParti}>
              <div className={styles.bilanTitle}>PARTI AVEC</div>
              <ul>
                {taken.map((item) => (
                  <li key={item}>
                    <FormattedInlineText text={item} />
                  </li>
                ))}
              </ul>
            </div>
            <div className={styles.bilanSauve}>
              <div className={styles.bilanTitle}>SAUVÉ</div>
              <ul>
                {saved.map((item) => (
                  <li key={item}>
                    <FormattedInlineText text={item} />
                  </li>
                ))}
              </ul>
            </div>
          </div>
        ) : null}
      </div>

      {stamp?.text ? (
        <div className={`${styles.stampWrap} cn-zoom-ui`}>
          <Stamp
            text={stamp.text}
            sub={stamp.sub}
            tone="arrest"
            drop={!instant}
            dropDelayMs={instant ? 0 : 900}
            instant={instant}
          />
        </div>
      ) : null}

      <div className={`${styles.foot} cn-zoom-ui`}>
        <button
          type="button"
          className={`${styles.continueBtn} ${styles.seqButton}`}
          onClick={handleContinueClick}
        >
          {continueText}
        </button>
        {nextHint ? <div className={styles.nextHint}>{nextHint}</div> : null}
      </div>
    </div>
  );
});

/** Resolve serial display from raw serial block. */
export function resolveRecordCardSerialDisplay(
  serial: { serie: string; n: number; total: number } | undefined,
): { serie?: string; number: string | null } {
  return {
    serie: serial?.serie,
    number: formatRecordCardSerialNumber(serial),
  };
}
